import {useMemo} from "react"
import type {RegionCode} from "@casas-diamante/types"
import {PropertyResponse as Property} from "@/types/Property"
import type {SelectedScheme} from "@/types/SearchScheme"

const PROPERTY_SEARCH_STATE_KEY = "property-search-state"

export type PropertySearchState = {
   source: "properties" | "condo"
   title?: string
   selectedPropertyId?: string
   searchString?: string
   partners?: string[]
   suspended?: string | null
   available?: string | null
   searchPage?: number
   inmuebles: Property[]
   totalHits?: number
   noMoreInm?: boolean
   scrollY: number
   returnTo: string
   // Region is part of the query and must travel with a restored property search.
   region?: RegionCode
   status?: string[]
   condoClass?: string[]
   scheme?: SelectedScheme
}

type BuildStateParams = {
   source: "properties" | "condo"
   returnTo: string
   region?: RegionCode
   inmuebles: Property[]
   scrollY?: number
   title?: string
   selectedPropertyId?: string
   searchString?: string
   partners?: string[]
   suspended?: string | null
   available?: string | null
   searchPage?: number
   totalHits?: number
   noMoreInm?: boolean
   status?: string[]
   condoClass?: string[]
   scheme?: SelectedScheme
}

export function buildPropertySearchState(params: BuildStateParams): PropertySearchState {
   return {
      source: params.source,
      returnTo: params.returnTo,
      title: params.title,
      selectedPropertyId: params.selectedPropertyId,
      searchString: params.searchString,
      partners: params.partners ?? [],
      suspended: params.suspended ?? null,
      available: params.available ?? null,
      searchPage: params.searchPage ?? 0,
      inmuebles: params.inmuebles,
      totalHits: params.totalHits ?? 0,
      noMoreInm: params.noMoreInm ?? false,
      scrollY: params.scrollY ?? window.scrollY,
      region: params.region,
      status: params.status ?? [],
      condoClass: params.condoClass ?? [],
      scheme: params.scheme ?? null,
   }
}

export function usePropertySearchState(params: BuildStateParams) {
   return useMemo(() => buildPropertySearchState(params), [params])
}

export function persistPropertySearchState(state: PropertySearchState) {
   sessionStorage.setItem(PROPERTY_SEARCH_STATE_KEY, JSON.stringify(state))
}

export function readPersistedPropertySearchState(): PropertySearchState | null {
   const value = sessionStorage.getItem(PROPERTY_SEARCH_STATE_KEY)

   if (!value) return null

   try {
      return JSON.parse(value) as PropertySearchState
   } catch {
      sessionStorage.removeItem(PROPERTY_SEARCH_STATE_KEY)
      return null
   }
}

export function clearPersistedPropertySearchState() {
   sessionStorage.removeItem(PROPERTY_SEARCH_STATE_KEY)
}

export type {BuildStateParams}
